import React, { useState, useContext, useMemo, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { useAccount } from 'wagmi';

import { MobileContext } from 'context';
import { useHistoryLength } from 'utils/Hooks';
import { HISTORY_ENTRIES_PER_PAGE } from 'const';
import { DUMMY_TOTAL_HIST_LENGTH } from 'utils/Hooks/useHistoryLength';
import HistoryPage from './page';
import CurrencySwitcher from 'utils/Currency/switcher';
import CryingDoge from 'assets/art/cryingDoge.png';
import './styles.scss';


function HistoryManager(props) {
  const isMobile = useContext(MobileContext);
  const totalHistLength = useHistoryLength();

  const account = useAccount();

  const [loadedPages, SetLoadedPages] = useState([]);
  const [currentPage, SetCurrentPage] = useState(0);
  const [areAllPagesLoaded, SetAllPagesLoaded] = useState(false);

  const { ref: pageBoundaryDetectorRef, inView: isPageBoundaryInView, entry
  } = useInView({ threshold: 0, delay: 250 });

  useEffect(FullyLoaded
    , [totalHistLength]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isPageBoundaryInView)
      LoadNextPage();
  }, [isPageBoundaryInView])


  async function LoadNextPage() {
    const newPageN = currentPage + 1;
    const [pageStart, pageLength] = DeterminePageBoundsFor(newPageN);
    if (pageStart == -1 && pageLength == -1)
      return;
    const nextPage = (<HistoryPage
      key={newPageN}
      number={newPageN}
      start={pageStart}
      length={pageLength}
      size={HISTORY_ENTRIES_PER_PAGE}
      totalHistoryLength={totalHistLength}
    />);
    SetCurrentPage(currentPage + 1);
    SetLoadedPages([...loadedPages, nextPage]);
  }


  function DeterminePageBoundsFor(pageNumber) {
    // pages go in ascending order; smart contract returns bet info in descending order
    // this function helps remedy this inconsistency 
    if (!pageNumber) throw new SyntaxError('missing required argumen: pageNumber');
    const pageNumberFromEnd = (NumberOfPages() + 1) - pageNumber;
    const thisIsTheLastPage = (pageNumberFromEnd === 1);
    const lastPageIsShorterThanOthers = (LastPageOffset() !== 0)

    let start, length;
    if (pageNumber > NumberOfPages()) {
      start = -1;
      length = -1;
      SetAllPagesLoaded(true);
    } else if (totalHistLength < HISTORY_ENTRIES_PER_PAGE) {
      start = 0;
      length = totalHistLength;
      SetAllPagesLoaded(true);
    } else if (thisIsTheLastPage && lastPageIsShorterThanOthers) {
      start = 0;
      length = HISTORY_ENTRIES_PER_PAGE - LastPageOffset();
      SetAllPagesLoaded(true);
    } else {
      start = (pageNumberFromEnd * HISTORY_ENTRIES_PER_PAGE - HISTORY_ENTRIES_PER_PAGE) - LastPageOffset();
      length = HISTORY_ENTRIES_PER_PAGE;
    }
    return [start, length];
  }

  function NumberOfPages() {
    const nOfPages = Math.ceil(totalHistLength / HISTORY_ENTRIES_PER_PAGE);
    return (nOfPages ? nOfPages : 1);
  }

  function LastPageOffset() {
    return (NumberOfPages() * HISTORY_ENTRIES_PER_PAGE - totalHistLength);
  }

  function FullyLoaded() {
    if (totalHistLength !== DUMMY_TOTAL_HIST_LENGTH)
      props.OnLoad();
  }

  /* JSX functions below */

  const connectWalletNotice = (<div className='HistoryManager Empty'>
    <CurrencySwitcher disabled={true} />
    <h1>Please connect your wallet</h1>
  </div>);

  const historyLoadingNotice = (<div className='HistoryManager Empty'>
    <CurrencySwitcher disabled={true} />
    <h1>Loading pages...</h1>
  </div>);

  const historyEmptyNotice = (<div className='HistoryManager Empty'>
    <CurrencySwitcher disabled={true} />
    <img src={CryingDoge} alt='crying doge' />
    <h2 className='bold'>We're Sorry</h2>
    <span className='bold'>Your history is empty!</span>
    <br />
    'Why not make some bets?'
  </div>);

  const mHistoryHeader = useMemo(() => {
    return (<thead>
      <tr className='Header1'>
        <th rowSpan='2' colSpan='1'>Round #</th>
        <th rowSpan='1' colSpan='2' className='PositionHeader'>Position</th>
        <th rowSpan='2' colSpan='2'>Wager</th>
        <th rowSpan='2' colSpan='2'>Result</th>
        <th rowSpan='2' colSpan='2'>Action</th>
      </tr>
      <tr className='Header2'>
        <th colSpan='1'>Entered</th>
        <th colSpan='1'>Closed</th>
      </tr>
    </thead>);
  }, []);

  const mInfinteScrollDetector = useMemo(() => {
    if (areAllPagesLoaded)
      return (<p className='HistoryEnd'>(no more rounds)</p>);
    else
      return (<div ref={pageBoundaryDetectorRef} id='PageBoundaryDetector' />);
  }, [areAllPagesLoaded]);



  if (!account?.address)
    return connectWalletNotice;
  else if (totalHistLength == DUMMY_TOTAL_HIST_LENGTH)
    return historyLoadingNotice;
  else if (totalHistLength == 0)
    return historyEmptyNotice;
  else
    return (
      <div className={'HistoryManager' + (isMobile ? ' Mobile' : '')}>
        <h2>
          <CurrencySwitcher />
          History
        </h2>
        <p className='ClickRoundIdHint'>Click on each round # to see more details</p>
        <div className='Page'>
          <table className='Results'>
            {mHistoryHeader}
            <tbody>
              {loadedPages}
            </tbody>
          </table>
        </div>
        {mInfinteScrollDetector}
      </div>
    );
}

export default HistoryManager;